/**
 * Home Visit Massager Page (訪問予定表 施術師毎 — /visits/massager_init).
 *
 * Legacy RA2 ⇢ VisitController@initReserveMassagers / RVisitController@initReserveMassagers.
 * One row per visit (no grouping); print/CSV use the same form filters.
 *
 * A2/R2 split resolved via useTheme().serviceType (card title, labels, file names).
 * Search state persisted in URL query (`run=1&visit_date_from=...&massager_id=...`)
 * so the page survives reload and the URL is shareable — mirrors visitpatient_init.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppButton, AppCheckbox } from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import BottomBar from '@/components/organisms/Footer';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import {
  useHomeVisitMassagerSearch,
  useHomeVisitMassagerMassagers,
} from '@/hooks/main/useHomeVisitMassager';
import { usePrintHomeVisitMassagerMutation } from '@/hooks/main/usePrintHomeVisitMassagerMutation';
import { useExportHomeVisitMassagerCsvMutation } from '@/hooks/main/useExportHomeVisitMassagerCsvMutation';
import HomeVisitMassagerSearchForm from './components/HomeVisitMassagerSearchForm';
import type { MassagerSearchFormValues } from './components/HomeVisitMassagerSearchForm';
import HomeVisitMassagerTable from './components/HomeVisitMassagerTable';
import type { HomeVisitMassagerSearchParams } from './types';
import {
  formValuesToSearchParams,
  searchParamsToFormValues,
  formValuesToSearchPayload,
} from './urlParams';

const PAGE_SIZE = 20;
const LS_SEARCH_KEY = 'visits.massager_init.search';

function persistSearchToLocalStorage(values: MassagerSearchFormValues) {
  try {
    localStorage.setItem(LS_SEARCH_KEY, JSON.stringify(values));
  } catch {
    // ignore quota / privacy mode
  }
}

function loadSearchFromLocalStorage(): MassagerSearchFormValues | null {
  try {
    const raw = localStorage.getItem(LS_SEARCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MassagerSearchFormValues>;
    if (
      typeof parsed?.visitDateFrom === 'string' &&
      typeof parsed?.visitDateTo === 'string' &&
      (parsed.massagerRadio === '1' || parsed.massagerRadio === '2')
    ) {
      return {
        visitDateFrom: parsed.visitDateFrom,
        visitDateTo: parsed.visitDateTo,
        massagerRadio: parsed.massagerRadio,
        massagerId: typeof parsed.massagerId === 'number' ? parsed.massagerId : null,
      };
    }
    return null;
  } catch {
    return null;
  }
}

const HomeVisitMassagerPage: React.FC = () => {
  const { serviceType } = useTheme();
  const sidebarOpen = useSidebarOpen();

  const isA2 = serviceType === SERVICE_TYPE_A2;
  const cardTitle = isA2 ? '予定表(はり・きゅう師毎)検索' : '予定表(マッサージ師毎)検索';

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Hydrate form defaults from URL once on first render. null = built-in defaults.
  const initialFormValues = useMemo(
    () => searchParamsToFormValues(urlSearchParams),
    // Intentional: run-once on mount; navigations re-mount the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [searchParams, setSearchParams] = useState<HomeVisitMassagerSearchParams | null>(() => {
    if (!initialFormValues) return null;
    return { ...formValuesToSearchPayload(initialFormValues), page: 1, per_page: PAGE_SIZE };
  });
  const [hasSearched, setHasSearched] = useState(() => initialFormValues !== null);

  const [printClinicName, setPrintClinicName] = useState(true);
  const [printClinicAddress, setPrintClinicAddress] = useState(true);

  const lastSearchValues = useRef<MassagerSearchFormValues | null>(initialFormValues);

  const { massagers, refetch: refetchMassagers } = useHomeVisitMassagerMassagers();
  const { rows, pagination, isLoading } = useHomeVisitMassagerSearch(searchParams);
  const { triggerPrint, isLoading: isPrinting } = usePrintHomeVisitMassagerMutation();
  const { triggerExport, isLoading: isExporting } = useExportHomeVisitMassagerCsvMutation();

  const handleSearch = useCallback(
    async (values: MassagerSearchFormValues) => {
      // BD: if the selected practitioner is no longer in the new list (because it expired / is hidden /
      // was soft-deleted between searches), refetch the dropdown and auto-fallback to the first option.
      let correctedValues = values;
      if (values.massagerRadio === '2') {
        const refetchResult = await refetchMassagers();
        const freshList = refetchResult.data?.data?.data ?? [];
        const stillValid =
          values.massagerId != null && freshList.some(m => m.id === values.massagerId);
        if (!stillValid) {
          correctedValues = { ...values, massagerId: freshList[0]?.id ?? null };
        }
      }
      lastSearchValues.current = correctedValues;
      persistSearchToLocalStorage(correctedValues);
      const payload = formValuesToSearchPayload(correctedValues);
      const params: HomeVisitMassagerSearchParams = { ...payload, page: 1, per_page: PAGE_SIZE };
      setSearchParams(params);
      setHasSearched(true);
      setUrlSearchParams(formValuesToSearchParams(correctedValues, params), { replace: true });
    },
    [refetchMassagers, setUrlSearchParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (!searchParams) return;
      setSearchParams(prev => (prev ? { ...prev, page } : null));
    },
    [searchParams]
  );

  // BD §6.3 / §6.4: PDF & CSV read search conditions from localStorage,
  // fallback to in-memory state to preserve UX when LS is unavailable (privacy mode, quota...).
  const handlePrint = useCallback(() => {
    const sv = loadSearchFromLocalStorage() ?? lastSearchValues.current;
    if (!sv) return;
    triggerPrint({
      ...formValuesToSearchPayload(sv),
      print_clinic_name: printClinicName,
      print_clinic_address: printClinicAddress,
    });
  }, [printClinicName, printClinicAddress, triggerPrint]);

  const handleExportCsv = useCallback(() => {
    const sv = loadSearchFromLocalStorage() ?? lastSearchValues.current;
    if (!sv) return;
    triggerExport(formValuesToSearchPayload(sv));
  }, [triggerExport]);

  const totalCount = pagination?.total ?? 0;
  const hasResults = rows.length > 0;

  return (
    <div className="pb-4">
      <HomeVisitMassagerSearchForm
        onSearch={handleSearch}
        onDateFromChange={() => refetchMassagers()}
        onDateToChange={() => refetchMassagers()}
        massagers={massagers}
        serviceType={serviceType}
        isLoading={isLoading}
        cardTitle={cardTitle}
        initialValues={initialFormValues}
        totalCount={totalCount}
      />

      {hasSearched ? (
        <CollapsibleCard>
          <HomeVisitMassagerTable
            data={rows}
            pagination={pagination}
            onPageChange={handlePageChange}
            isLoading={isLoading}
            hasSearched={hasSearched}
            serviceType={serviceType}
          />
        </CollapsibleCard>
      ) : null}

      <BottomBar
        variant="action"
        visible={hasResults}
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <div className="flex items-center gap-3">
            <AppCheckbox
              checked={printClinicName}
              onChange={e => setPrintClinicName(e.target.checked)}
            >
              <span className="text-white">治療院名印刷</span>
            </AppCheckbox>
            <AppCheckbox
              checked={printClinicAddress}
              onChange={e => setPrintClinicAddress(e.target.checked)}
            >
              <span className="text-white">治療院住所・電話番号印刷</span>
            </AppCheckbox>
          </div>
        }
        right={
          <div className="flex gap-3">
            <AppButton
              type="primary"
              className="rounded"
              disabled={!hasResults}
              loading={isPrinting}
              onClick={handlePrint}
            >
              一覧印刷
            </AppButton>
            <AppButton
              type="primary"
              className="rounded"
              disabled={!hasResults}
              loading={isExporting}
              onClick={handleExportCsv}
            >
              CSV出力
            </AppButton>
          </div>
        }
      />
    </div>
  );
};

export default HomeVisitMassagerPage;
